import { Operation, type TOperation } from '@dbl/core/filter';

import type {
  IColor,
  IMetricOption,
  INumericalValueItem,
  INumericalValueRule,
} from '../type';
import {
  DEFAULT_RULE_COLOR,
  LEGACY_OPERATOR_MAP,
  RULE_COLORS,
} from './constants';
import { normalizeNumericalIconValue } from './NumericalValuesGrid/NumericalIconSelect/constants';

let ruleIdSeed = 0;

export const createRuleId = (): string => {
  ruleIdSeed += 1;
  return `numerical-rule-${Date.now()}-${ruleIdSeed}`;
};

const mkRule = (
  id: string,
  operation: TOperation,
  value: string[],
  color: IColor,
  icon: string,
): INumericalValueRule => ({
  id,
  operation,
  value,
  color,
  icon,
});

export const createEmptyRule = (): INumericalValueRule => ({
  id: createRuleId(),
  operation: Operation.Greater,
  value: [],
  color: DEFAULT_RULE_COLOR,
  icon: 'rising-arrow-icon',
});

/** 新选指标默认三条规则：>0 绿↑、<0 红↓、=0 灰— */
export const createDefaultRules = (): INumericalValueRule[] => [
  mkRule(
    createRuleId(),
    Operation.Greater,
    ['0'],
    RULE_COLORS.green,
    'rising-arrow-icon',
  ),
  mkRule(
    createRuleId(),
    Operation.Less,
    ['0'],
    RULE_COLORS.red,
    'down-arrow-icon',
  ),
  mkRule(
    createRuleId(),
    Operation.Equal,
    ['0'],
    RULE_COLORS.gray,
    'jian',
  ),
];

export const createNumericalGroupFromMetric = (
  id: string,
  selectItem?: Pick<
    IMetricOption,
    'column_display' | 'column_name' | 'table_code'
  >,
): INumericalValueItem => ({
  id,
  table_code: selectItem?.table_code || 't0',
  metric: selectItem?.column_name,
  name: selectItem?.column_display || selectItem?.column_name,
  rules: createDefaultRules(),
});

/** 写入 draft 时剥离 rules[].id，并统一为 name / table_code 结构 */
export const stripNumericalValuesForPersist = (
  groups: INumericalValueItem[],
): INumericalValueItem[] =>
  groups.map(group => ({
    id: group.id,
    name: group.name || group.indicator || group.metric,
    metric: group.metric,
    table_code: group.table_code || 't0',
    rules: (group.rules || []).map(rule => {
      const { id: _ruleId, ...persisted } = rule as INumericalValueRule;
      return persisted;
    }),
  }));

type TLegacyRule = INumericalValueRule & {
  operator?: string;
  value?: string[] | number | null;
  color?: IColor | string;
};

const toStringValues = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map(item => String(item));
  }
  if (value === null || value === undefined || value === '') {
    return [];
  }
  return [String(value)];
};

const resolveLegacyColor = (color: unknown): IColor => {
  if (color && typeof color === 'object' && 'hexa' in color) {
    return color as IColor;
  }
  if (typeof color === 'string' && color in RULE_COLORS) {
    return RULE_COLORS[color as keyof typeof RULE_COLORS];
  }
  return DEFAULT_RULE_COLOR;
};

export const normalizeRule = (rule: TLegacyRule): INumericalValueRule => {
  if (
    rule.operation &&
    rule.color &&
    typeof rule.color === 'object' &&
    'hexa' in rule.color
  ) {
    return {
      id: rule.id || createRuleId(),
      operation: rule.operation,
      value: toStringValues(rule.value),
      color: rule.color,
      icon: normalizeNumericalIconValue(rule.icon),
    };
  }

  const legacyOperator = rule.operator;
  const operation =
    (legacyOperator && LEGACY_OPERATOR_MAP[legacyOperator]) ||
    rule.operation ||
    Operation.Greater;

  return {
    id: rule.id || createRuleId(),
    operation,
    value: toStringValues(rule.value),
    color: resolveLegacyColor(rule.color),
    icon: normalizeNumericalIconValue(rule.icon),
  };
};

export const normalizeNumericalGroups = (
  value?: INumericalValueItem[],
): INumericalValueItem[] => {
  if (!value?.length) {
    return [];
  }
  return value.map(group => ({
    ...group,
    name: group.name || group.indicator || group.metric,
    metric: group.metric,
    table_code: group.table_code || 't0',
    rules: group.rules?.length
      ? group.rules.map(rule => normalizeRule(rule as TLegacyRule))
      : createDefaultRules(),
  }));
};

